import { Component, OnInit, OnDestroy } from '@angular/core';

import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { HelperService, CalculationService, DataService, HTTPService } from 'app/services';

import { debounce } from 'lodash';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-service-lookup',
  templateUrl: './service-lookup.component.html',
  styleUrls: ['./service-lookup.component.scss'],
  providers: [DialogService],
})
export class ServiceLookupComponent implements OnInit, OnDestroy {
  activeTabIndex: number = 0;
  openVehicleInSidepanel: any;
  query: string | null = null;
  isSearchFieldFocused: boolean = false;
  isMitchellIntegrationEnabled: boolean = false;
  isMitchell1Loading: boolean = false;
  isMitchell1Connected: boolean = false;
  dataListener: Subscription;
  confirmationTitle: string = 'Deselecting the service?';
  confirmationContent: string = 'Deselecting the service will reset all the quantities permanently';
  unlinkVehicleMessage: string = 'Connect a vehicle to view MOTOR canned services';
  searchVehicleBy: string = '';
  pricings: any = {};
  itemSettings: any = {};
  vehicle: any = {};
  motorVehicleId: string = '';
  isMotorServiceEnabled: boolean = true;
  isRepairDiagramEnabled: boolean = true;
  isCarfaxEnabled: boolean = false;
  isVehicleSearching: boolean = false;
  motorVehicle: any = {};
  mileage: any = {
    value: '',
    unit: null,
  };
  vehicleSuggestions: any[] = [];
  searchCannedServices = debounce(this._searchCannedServices, 800);
  defaultRegState: string = '';
  defaultRegStateCode: string = '';
  states: any[] = [];
  vehicleQuery: string = '';
  options: any = {};

  constructor(
    private dynamicDialogRef: DynamicDialogRef,
    private helperService: HelperService,
    public calculationService: CalculationService,
    private dataService: DataService,
    private httpService: HTTPService
  ) {}

  /** Fetching all services */
  ngOnInit() {
    this.isRepairDiagramEnabled = this.dataService.isRepairDiagramEnabled;
    this.isMitchellIntegrationEnabled = this.dataService.isMitchellIntegrationEnabled;

    this.getMotorTagPricings();
    this.getMitchellConnection();

    this.dataListener = this.dataService.getData().subscribe(({ data }) => {
      if (data.key === 'company-appdata-updated') {
        this.isRepairDiagramEnabled = this.dataService.isRepairDiagramEnabled;
        this.isMitchellIntegrationEnabled = this.dataService.isMitchellIntegrationEnabled;
      }
    });
  }

  ngOnDestroy() {
    if (this.dataListener) {
      this.dataListener.unsubscribe();
    }
  }

  getMotorTagPricings() {
    this.httpService.getDefaultMotorPricingsAndTags().then(
      (data) => {
        if (data.appsettings) {
          this.setMotorTypeSettings(data.appsettings, data.motorTag);
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  setMotorTypeSettings(appsettings: any, motorTag: any) {
    const {
      pricingId,
      partPriceId,
      isTaxablePart,
      isTaxableLabor,
      mBaseLaborCost,
      partQboItemRefId,
      laborQboItemRefId,
      percisionLaborCost,
      maintainenacePriceId,
      percisionLaborPriceId,
      maintainenaceLaborCost,
      displayDescOnOrderPart,
      displayHoursOnOrderPart,
      displayDescOnOrderLabor,
      displayHoursOnOrderLabor,
    } = appsettings.appsettings;

    if (pricingId) {
      this.pricings.General = {
        price: pricingId.rate || 0,
        cost: mBaseLaborCost || 0,
        pricing: {
          pricingId: pricingId._id,
          name: pricingId.name,
        },
      };
    }

    if (percisionLaborPriceId) {
      this.pricings.Precision = {
        price: percisionLaborPriceId.rate || 0,
        cost: percisionLaborCost || 0,
        pricing: {
          pricingId: percisionLaborPriceId._id,
          name: percisionLaborPriceId.name,
        },
      };
    }

    if (maintainenacePriceId) {
      this.pricings.Maintenance = {
        price: maintainenacePriceId.rate || 0,
        cost: maintainenaceLaborCost || 0,
        pricing: {
          pricingId: maintainenacePriceId._id,
          name: maintainenacePriceId.name,
        },
      };
    }

    this.itemSettings.part = {
      motorTag: motorTag,
      isTaxable: isTaxablePart,
      displayHoursOnOrder: displayHoursOnOrderPart,
      displayDescOnOrder: displayDescOnOrderPart,
      qboId: (partQboItemRefId && partQboItemRefId.qboId) || '',
      pricing: partPriceId,
    };
    this.itemSettings.labor = {
      motorTag: motorTag,
      isTaxable: isTaxableLabor,
      displayHoursOnOrder: displayHoursOnOrderLabor,
      displayDescOnOrder: displayDescOnOrderLabor,
      qboId: (laborQboItemRefId && laborQboItemRefId.qboId) || '',
    };
  }

  setServiceTotals(service: any) {
    const data = {
      hours: 0,
      parts: 0,
      tires: 0,
      other: 0,
      partCost: 0,
      laborCost: 0,
      tireCost: 0,
      otherCost: 0,
      isAdded: false,
    };

    if (service.items && service.items.length) {
      service.items.forEach((item: any) => {
        if (item.type === 'labor') {
          data.hours += 1 * item.count;
          data.laborCost += this.calculationService.roundOff(item.subTotal);
        }

        if (item.type === 'part') {
          data.parts += 1 * item.count;
          data.partCost += this.calculationService.roundOff(item.subTotal);
        }

        if (item.type === 'tire') {
          data.tires += 1 * item.count;
          data.tireCost += this.calculationService.roundOff(item.subTotal);
        }

        if (item.type === 'other') {
          data.other += 1 * item.count;
          data.otherCost += this.calculationService.roundOff(item.subTotal);
        }
      });
    }

    return data;
  }

  setInspectionCounts(inspection: any = {}) {
    let categoryCount = 0;
    let itemCount = 0;
    let isItemExists = false;

    if (inspection.inspectionId && inspection.inspectionId.category && inspection.inspectionId.category.length) {
      inspection.inspectionId.category.forEach((category: any) => {
        if (category.inspectionItems.length) {
          category.inspectionItems.forEach(({ isChecked }: any) => {
            if (isChecked) {
              itemCount += 1;
              isItemExists = true;
            }
          });
        }

        if (isItemExists) {
          categoryCount += 1;
          isItemExists = false;
        }
      });
    }

    return {
      categoryCount,
      itemCount,
    };
  }

  /** Searching canned services locally */
  _searchCannedServices(value: string = '', forceTrigger: boolean = false) {
    if (value === this.query && !forceTrigger) {
      return;
    }

    this.dataService.sendData(
      this.dataService.typePassData('motor-on-search', {
        query: value && value.toLowerCase(),
      })
    );

    this.query = value;
  }

  onStateSelect({ state, stateCode }, triggerSearch: boolean = false) {
    this.vehicle.registrationState = state;
    this.vehicle.registrationStateCode = stateCode;
    this.vehicle.state = state;

    if (triggerSearch) {
      this.onSearchVehicle(this.vehicleQuery);
    }
  }

  /** On vehicle search */
  onSearchVehicle(query: string) {
    this.vehicleQuery = query;
    this.isVehicleSearching = true;
    this.httpService
      .searchMotorVehicle(query && query.toLowerCase(), this.vehicle.registrationStateCode || this.defaultRegStateCode, false)
      .then(
        (result: any) => {
          this.searchVehicleBy = '';
          if (!result) return;

          this.searchVehicleBy = result.SearchType;
          this.vehicleSuggestions = [...result.Vehicles.map((vehicle: any) => this.helperService.setVehicleMotorSearchResults(vehicle, this.vehicleQuery))];
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isVehicleSearching = false;
      });
  }

  onVehicleSelect(vehicle: any) {
    if (vehicle && vehicle.BaseVehicleID) {
      this.motorVehicle = vehicle;
      this.motorVehicleId = vehicle.BaseVehicleID;
      this.vehicleQuery = `${vehicle.Year || ''} ${vehicle.MakeName || ''} ${vehicle.ModelName || ''}`;
      this.query = '';
      this.options = {};

      if (this.searchVehicleBy === 'VIN') {
        this.options.VIN = this.vehicleQuery;
      } else {
        this.options.year = vehicle.Year;
        this.options.make = vehicle.MakeName;
        this.options.model = vehicle.ModelName;
        this.options.engine = vehicle.EngineDescription;
      }

      this._searchCannedServices('', true);
    }
  }

  handleSelectTab(activeTabIndex: number) {
    if (this.activeTabIndex === activeTabIndex) return;

    if (this.activeTabIndex === 2 || activeTabIndex === 2) {
      this.query = '';
    }

    if (activeTabIndex === 3 && !this.isMitchell1Connected) return;

    this.activeTabIndex = activeTabIndex;
    setTimeout(() => {
      this.searchCannedServices(this.query, true);
    }, 200);
    this.isSearchFieldFocused = true;
  }

  handleClose() {
    this.dynamicDialogRef.close({});
  }

  handleOpenMitchell1Guide() {
    if (this.isMitchell1Loading) return;

    this.isMitchell1Loading = true;
    this.httpService
      .getMitchell1GuideLink({ vehicleId: this.vehicle.vehicleId, searchTerm: encodeURIComponent(this.query), ...this.options })
      .then(
        (url) => {
          if (url) {
            window.open(url, '', `width=${window.outerWidth},height=${window.outerHeight}`);
          }
        },
        (err) => {
          this.helperService.showToaster('error', err);
        }
      )
      .finally(() => {
        this.isMitchell1Loading = false;
      });
  }

  getMitchellConnection() {
    this.httpService.isMitchell1Connected().then(
      (data: any) => {
        if (data) {
          this.isMitchell1Connected = data.isMitchell1Connected;
        } else {
          this.isMitchell1Connected = false;
        }
      },
      (err) => {}
    );
  }
}
